import {
  faEllipsisV,
  faEnvelopeOpen,
  faPencil,
} from '@fortawesome/pro-regular-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { logoutUser } from '@gen2/api/auth/api';
import { InviteListingKeys } from '@gen2/api/invite-listing/hooks';
import { TInvite } from '@gen2/api/invites/api';
import { useEditDraftInviteMutation } from '@gen2/api/invites/hooks';
import { handleAvatar } from '@gen2/app/components/avatar/avatar';
import CurrentAccount from '@gen2/app/components/current-account/current-account';
import { useInviteListingItemStore } from '@gen2/app/invite-listing/invite-listing-item/store';
import { useSendInviteApi } from '@gen2/app/invites/send-invites/hooks';
import { useSendInviteStore } from '@gen2/app/invites/send-invites/store';
import { useMergeTemplateModalStore } from '@gen2/app/invites/send-invites/templates/merge-templates-modal/store';
import { queryClient } from '@gen2/config/queryClient';
import { useAuth, useRouter } from '@gen2/hooks';
import { useSidebarStore } from '@gen2/hooks/stores';
import { usePageTitle } from '@gen2/hooks/usePageTitle';
import { PermissionsKeys } from '@gen2/types/permissions';
import { Permissions } from '@gen2/utils/permissions/permissions';
import { Button, Menu, MenuItem, useMediaQuery, useTheme } from '@mui/material';
import { useEffect, useMemo, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import {
  accountHeader,
  archivedListingHeader,
  brandingHeader,
  collationHistoryHeader,
  contactGroupsHeader,
  contactsHeader,
  createContactGroupsHeader,
  createContactsHeader,
  createInviteHeader,
  createMembersHeader,
  createTeamsHeader,
  createTemplateHeader,
  CustomHeader,
  dashBoardHeader,
  draftListingHeader,
  editContactGroupsHeader,
  editContactHeader,
  editInviteHeader,
  editTeamsHeader,
  emptyHeader,
  fileOptionHeader,
  integrationsHeader,
  manageInviteHeader,
  viewInviteHeader,
  rolesPermissionsHeader,
  teamMembersHeader,
  teamsHeader,
  templatesHeader,
  updateMembersHeader,
  viewContactGroupsHeader,
  viewContactHeader,
  viewTeamMembersHeader,
  viewTeamsHeader,
  viewTemplateHeader,
  workspacesHeader,
  notificationsHeader,
  notificationPreferencesHeader,
  managementInsightsHeader,
  portalAuthenticationHeader,
  usageAndBilling,
} from './data';
import {
  HeaderTitle,
  StyledAvatar,
  StyledHeader,
  StyledHeaderActions,
  StyledNavLink,
  StyledSubMenuToggle,
  SubTitle,
  InviteName,
  StyledInput,
  InviteNameBox,
} from './header.styled';
import { useUpdateInviteName } from './hooks';

const MAX_INVITE_NAME_LENGTH = 100;
export const CustomizedHeader = ({
  headerTitle,
  subTitle = '',
  isShowInviteName = false,
}: CustomHeader) => {
  const router = useRouter();

  const isSendInvite = router.pathname.startsWith('/send-invites');

  const { inviteIsMerging } = useMergeTemplateModalStore();

  const sendInviteStore = useSendInviteStore();
  const inviteListingItemStore = useInviteListingItemStore();

  const invite = isSendInvite
    ? sendInviteStore.invite
    : inviteListingItemStore.invite;

  const isLoading = isSendInvite
    ? sendInviteStore.isLoadingInvite
    : inviteListingItemStore.isLoadingInvite;

  const isFetching = isSendInvite
    ? sendInviteStore.isFetchingInvite
    : inviteListingItemStore.isFetchingInvite;

  const [inviteNameValue, setInviteNameValue] = useState(
    invite?.reference ?? null,
  );
  usePageTitle(undefined, [invite?.reference ?? 'Untitled Invite']);
  const [isEditInviteName, setIsEditInviteName] = useState(false);
  const { onUpdateInviteName, isUpdating } = useUpdateInviteName();
  const { mutate: updateInviteMutation, isLoading: isUpdatingInvite } =
    useEditDraftInviteMutation();
  const inputRef = useRef<HTMLInputElement>(null);
  useEffect(() => {
    setInviteNameValue(invite?.reference ?? null);
  }, [invite?.reference]);

  const handleEditInviteName = () => {
    if (sendInviteStore.isAutoSaveTrigger) {
      return;
    }

    setIsEditInviteName(true);
    if (inviteNameValue === 'Untitled Invite') {
      setInviteNameValue(null);
    }
  };

  const handleChangeInviteName = (e: React.ChangeEvent<HTMLInputElement>) => {
    if (sendInviteStore.isAutoSaveTrigger) {
      return;
    }

    setInviteNameValue(e.target.value);
  };

  const handleClickInviteName = (e: React.MouseEvent<HTMLInputElement>) => {
    if (sendInviteStore.isAutoSaveTrigger) {
      return;
    }

    e.stopPropagation();
    e.preventDefault();

    if (inputRef.current?.value === 'Untitled Invite') {
      inputRef.current.value = '';
    }

    inputRef.current?.focus();
  };

  const handleBlurInviteName = (e: React.FocusEvent<HTMLInputElement>) => {
    setIsEditInviteName(false);
    // update invite name if invite exists
    if (invite) {
      if (isSendInvite) {
        onUpdateInviteName({
          ...(invite as TInvite),
          reference: inviteNameValue || null,
        });
      } else {
        updateInviteMutation(
          {
            id: invite?.id ?? '',
            from_user_id: invite?.from_user?.id ?? '',
            subject: invite?.subject,
            message: invite?.message,
            due_at: invite?.due_at,
            meta: invite?.meta ?? '',
            overdue_reminder: invite?.overdue_reminder ?? 0,
            almost_due_reminder: invite?.almost_due_reminder ?? 0,
            assignee_user_id: invite?.assignee_user?.id ?? '',
            team_id: invite?.team?.id ?? '',
            reference: inviteNameValue || null,
          },
          {
            onSuccess: () => {
              queryClient.invalidateQueries([
                InviteListingKeys.queryInviteItem,
              ]);
            },
          },
        );
      }
    }
  };

  return (
    <div data-cy="header-breadcrumbs">
      <HeaderTitle data-cy="header-title">
        {headerTitle} <SubTitle data-cy="header-subtitle">{subTitle}</SubTitle>
        {/* invite name subtite only applied to View Invites, Create Invite, Edit Invite, Edit Draft Invite */}
        {isShowInviteName && (
          <InviteNameBox
            data-cy="header-invite-name"
            $disabled={
              isUpdating ||
              isLoading ||
              isFetching ||
              isUpdatingInvite ||
              inviteIsMerging ||
              sendInviteStore.isAutoSaveTrigger
            }
            onClick={handleEditInviteName}
          >
            |{' '}
            {isEditInviteName ? (
              <StyledInput
                autoFocus
                id="invite-name-input"
                name="invite-name-input"
                size="small"
                color="secondary"
                ref={inputRef}
                value={inviteNameValue ?? ''}
                onChange={handleChangeInviteName}
                onBlur={handleBlurInviteName}
                onClick={handleClickInviteName}
                inputProps={{ maxLength: MAX_INVITE_NAME_LENGTH }}
                disabled={
                  isUpdating ||
                  isLoading ||
                  isFetching ||
                  isUpdatingInvite ||
                  inviteIsMerging ||
                  sendInviteStore.isAutoSaveTrigger
                }
              />
            ) : (
              <InviteName>
                {invite?.reference ?? 'Untitled Invite'}{' '}
                <FontAwesomeIcon icon={faPencil} transform="right-2" />
              </InviteName>
            )}
          </InviteNameBox>
        )}
      </HeaderTitle>
    </div>
  );
};

export const Header = () => {
  const { t } = useTranslation();
  const theme = useTheme();
  const matches = useMediaQuery(theme.breakpoints.down('md'));
  const router = useRouter();
  const isEditInvite =
    router.pathname.startsWith('/send-invites/') &&
    router.pathname.split('/').length > 2;
  const isCreateInvite = router.pathname === '/send-invites';
  const isCreateTemplate =
    router.pathname.startsWith('/templates/') &&
    router.pathname.split('/').length > 2;

  const isManageInvite = router.pathname === '/invite-listing';
  const isViewInvite =
    router.pathname.startsWith('/invite-listing') &&
    router.pathname.split('/').length > 2;
  // updateInviteHeader
  // NOTE: order matters
  const renderBreadcrumbs = useMemo(() => {
    if (isManageInvite) {
      return manageInviteHeader;
    } else if (isViewInvite) {
      return viewInviteHeader;
    } else if (isCreateInvite) {
      return createInviteHeader;
    } else if (isEditInvite) {
      return editInviteHeader;
    } else if (router.pathname.includes('/invites/collation-history')) {
      return collationHistoryHeader;
    } else if (router.pathname.includes('/integrations')) {
      return integrationsHeader;
    } else if (router.pathname.includes('/team-members/member/')) {
      return updateMembersHeader;
    } else if (router.pathname.includes('/team-members/member')) {
      return createMembersHeader;
    } else if (router.pathname.includes('/team-members/')) {
      return viewTeamMembersHeader;
    } else if (router.pathname.includes('/team-members')) {
      return teamMembersHeader;
    } else if (router.pathname.includes('/templates/view')) {
      return viewTemplateHeader;
    } else if (isCreateTemplate) {
      return createTemplateHeader;
    } else if (router.pathname.includes('/templates')) {
      return templatesHeader;
    } else if (router.pathname.includes('/teams/create')) {
      return createTeamsHeader;
    } else if (router.pathname.includes('/teams/view')) {
      return viewTeamsHeader;
    } else if (router.pathname.includes('/teams/edit')) {
      return editTeamsHeader;
    } else if (router.pathname.includes('/teams')) {
      return teamsHeader;
    } else if (router.pathname.includes('/dashboard')) {
      return dashBoardHeader;
    } else if (router.pathname.includes('/branding')) {
      return brandingHeader;
    } else if (router.pathname.includes('/file-options')) {
      return fileOptionHeader;
    } else if (router.pathname.includes('/draft-listing')) {
      return draftListingHeader;
    } else if (router.pathname.includes('/archived-listing')) {
      return archivedListingHeader;
    } else if (router.pathname.includes('/account')) {
      return accountHeader;
    } else if (router.pathname.includes('/contacts/create')) {
      return createContactsHeader;
    } else if (router.pathname.includes('/contacts/view')) {
      return viewContactHeader;
    } else if (router.pathname.includes('/contacts/edit')) {
      return editContactHeader;
    } else if (router.pathname.includes('/contacts')) {
      return contactsHeader;
    } else if (router.pathname.includes('/contact-groups/create')) {
      return createContactGroupsHeader;
    } else if (router.pathname.includes('/contact-groups/view')) {
      return viewContactGroupsHeader;
    } else if (router.pathname.includes('/contact-groups/edit')) {
      return editContactGroupsHeader;
    } else if (router.pathname.includes('/contact-groups')) {
      return contactGroupsHeader;
    } else if (router.pathname.includes('/workspaces')) {
      return workspacesHeader;
    } else if (router.pathname.includes('/borrower-email-template-settings')) {
      return notificationsHeader;
    } else if (router.pathname.includes('/roles-permissions')) {
      return rolesPermissionsHeader;
    } else if (router.pathname.includes('/notification-preferences')) {
      return notificationPreferencesHeader;
    } else if (router.pathname.includes('/reporting')) {
      return managementInsightsHeader;
    } else if (router.pathname.includes('/portal-authentication')) {
      return portalAuthenticationHeader;
    } else if (router.pathname.includes('/usage-and-billing')) {
      return usageAndBilling;
    } else {
      return emptyHeader;
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [router.pathname]);
  const sidebarStore = useSidebarStore();

  // Jump to new invite page and the page will do not wait for a response.
  const { onNavigateToDraft } = useSendInviteApi();

  const { logout, user } = useAuth();

  const [anchorEl, setAnchorEl] = useState<null | HTMLElement>(null);
  const open = Boolean(anchorEl);
  const handleProfileClick = (event: React.MouseEvent<HTMLButtonElement>) => {
    setAnchorEl(event.currentTarget);
  };

  const handleClose = () => {
    setAnchorEl(null);
  };

  const onToggleSubMenu = () => {
    sidebarStore.setSelected({ id: 'invites' });
    sidebarStore.setSubMenuOpen(!sidebarStore.subMenuOpen);
  };

  const onLogout = async () => {
    try {
      await logoutUser();
    } catch (error) {
      console.error('Error during logout:', error);
    } finally {
      logout();
      handleClose();
    }
  };

  return (
    <StyledHeader data-cy="header">
      <StyledSubMenuToggle onClick={onToggleSubMenu}>
        <FontAwesomeIcon icon={faEllipsisV} />
      </StyledSubMenuToggle>
      <CustomizedHeader {...renderBreadcrumbs} />
      <StyledHeaderActions spacing={1} direction="row">
        <CurrentAccount user={user} />

        <Permissions allow={PermissionsKeys.CREATE_INVITE}>
          <Button
            data-cy="create-invite-btn"
            variant="contained"
            color="secondary"
            onClick={() => onNavigateToDraft('initial')}
          >
            {!matches ? (
              t('header.btn', { ns: 'common' })
            ) : (
              <FontAwesomeIcon icon={faEnvelopeOpen} />
            )}
          </Button>
        </Permissions>
        <StyledAvatar
          aria-haspopup="true"
          aria-expanded={open ? 'true' : undefined}
          onClick={handleProfileClick}
          data-cy="header-avatar-btn"
        >
          {handleAvatar(user)}
        </StyledAvatar>
        <Menu anchorEl={anchorEl} open={open} onClose={handleClose}>
          <MenuItem>
            <StyledNavLink to="/user-settings">Profile</StyledNavLink>
          </MenuItem>
          {user?.organisations &&
            (user.organisations.length > 1 ||
              user.organisations[0].accounts.length > 1) && (
              <MenuItem>
                <StyledNavLink to="/switch-organisations">
                  Switch Organisations
                </StyledNavLink>
              </MenuItem>
            )}
          <MenuItem onClick={onLogout}>Logout</MenuItem>
        </Menu>
      </StyledHeaderActions>
    </StyledHeader>
  );
};
